"use client"

import { useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import { CircularProgress, Accordion, AccordionSummary, AccordionDetails, Chip } from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import PrintIcon from "@mui/icons-material/Print"
import ClearIcon from "@mui/icons-material/Clear"
import { ToastContainer, toast } from "react-toastify"
import Select from "react-select"
import AssessmentIcon from "@mui/icons-material/Assessment"
import SchoolIcon from "@mui/icons-material/School"
import PeopleIcon from "@mui/icons-material/People"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import BookIcon from "@mui/icons-material/Book"
import nodata from "assets/images/nodata.png"
import "bootstrap/dist/css/bootstrap.min.css"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"

const BASE_URL = process.env.REACT_APP_BASE_URL
const token = sessionStorage.getItem("token")

function AdmissionSummaryReport() {
  const [loading, setLoading] = useState(false)
  const [campaignLoading, setCampaignLoading] = useState(false)
  const [allCampaigns, setAllCampaigns] = useState([])
  const [selectedCampaigns, setSelectedCampaigns] = useState([])
  const [summaryData, setSummaryData] = useState(null)
  const [expandedDepartments, setExpandedDepartments] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredData, setFilteredData] = useState(null)

  // Program level filter state
  const [programLevelFilter, setProgramLevelFilter] = useState("All")
  const programLevels = [
    "All",
    "Undergraduate (BS Programs)",
    "Graduate (MS, M-Phil Programs)",
    "Doctorate (PhD Programs)",
    "5th Semester",
  ]
  const programLevelOptions = programLevels.map((pl) => ({ value: pl, label: pl }))

  // Gender filter state
  const [genderFilter, setGenderFilter] = useState("All")
  const genderOptions = [
    { value: "All", label: "All Genders" },
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ]

  // Fetch all campaigns
  const fetchAllCampaigns = async () => {
    setCampaignLoading(true)
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
      const response = await fetch(`${BASE_URL}/campaign/get_campaigns`, requestOptions)
      const data = await response.json()
      if (response.ok) {
        setAllCampaigns(data.campaigns || [])
      } else {
        toast.error("Failed to fetch campaigns")
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error)
      toast.error("Error fetching campaigns")
    } finally {
      setCampaignLoading(false)
    }
  }

  const csvEscape = (val) => {
    if (val === null || val === undefined) return ""
    const str = String(val)
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const quotasToString = (quotas) => {
    if (!quotas || typeof quotas !== "object") return ""
    try {
      return Object.entries(quotas)
        .map(([qName, qData]) => `${qName}: ${qData?.filled_seats ?? 0}/${qData?.total_seats ?? 0}`)
        .join("; ")
    } catch {
      return ""
    }
  }

  const sumFromDepartments = (departments = []) => {
    const totals = departments.reduce(
      (acc, d) => {
        acc.total += Number(d.total_seats || 0)
        acc.filled += Number(d.filled_seats || 0)
        return acc
      },
      { total: 0, filled: 0 },
    )
    return {
      total_seats: totals.total,
      filled_seats: totals.filled,
    }
  }

  const buildCsvRows = (dataSource) => {
    // Header
    const rows = [
      ["Level", "Department", "Program", "Total Seats", "Filled Seats", "Available Seats", "Fill %", "Quotas"],
    ]
    const departments = dataSource?.departments || []

    // Choose overall totals to reflect what's displayed:
    // Prefer dataSource.grand_total; if missing, compute from visible departments.
    const overall = dataSource?.grand_total
      ? {
          total_seats: Number(dataSource.grand_total.total_seats || 0),
          filled_seats: Number(dataSource.grand_total.filled_seats || 0),
          quotas: dataSource.grand_total.quotas || null,
        }
      : (() => {
          const t = sumFromDepartments(departments)
          return { ...t, quotas: null }
        })()

    const overallAvailable = (overall.total_seats || 0) - (overall.filled_seats || 0)
    const overallPct = calculateFillPercentage(overall.filled_seats, overall.total_seats)

    rows.push([
      "Overall",
      "",
      "",
      csvEscape(overall.total_seats),
      csvEscape(overall.filled_seats),
      csvEscape(overallAvailable),
      csvEscape(`${overallPct}`),
      csvEscape(quotasToString(overall.quotas)),
    ])

    // Department-level rows
    departments.forEach((dept) => {
      const dTotal = Number(dept.total_seats || 0)
      const dFilled = Number(dept.filled_seats || 0)
      const dAvail = dTotal - dFilled
      const dPct = calculateFillPercentage(dFilled, dTotal)

      rows.push([
        "Department",
        csvEscape(dept.name || ""),
        "",
        csvEscape(dTotal),
        csvEscape(dFilled),
        csvEscape(dAvail),
        csvEscape(`${dPct}`),
        csvEscape(quotasToString(dept.quotas)),
      ])

      // Program-level rows
      ;(dept.programs || []).forEach((prog) => {
        const pTotal = Number(prog.total_seats || 0)
        const pFilled = Number(prog.filled_seats || 0)
        const pAvail = pTotal - pFilled
        const pPct = calculateFillPercentage(pFilled, pTotal)

        rows.push([
          "Program",
          csvEscape(dept.name || ""),
          csvEscape(prog.name || ""),
          csvEscape(pTotal),
          csvEscape(pFilled),
          csvEscape(pAvail),
          csvEscape(`${pPct}`),
          csvEscape(quotasToString(prog.quotas)),
        ])
      })
    })

    return rows
  }

  const handleExportCsv = () => {
    const dataSource = filteredData || summaryData
    if (!dataSource || !dataSource.departments) {
      toast.error("No report data available to export")
      return
    }
    try {
      const rows = buildCsvRows(dataSource)
      const csv = rows.map((r) => r.join(",")).join("\n")
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)

      const campaignNames = (summaryData?.campaign_names || []).join("_") || "report"
      const dateStr = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
      const filename = `admission-summary-${campaignNames}-${dateStr}.csv`

      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success("CSV exported successfully")
    } catch (err) {
      console.error("CSV export failed:", err)
      toast.error("Failed to export CSV")
    }
  }

  // Fetch admission summary report for multiple campaigns
  const fetchAdmissionSummary = async (campaignIds, programLevel, gender) => {
    if (!campaignIds || campaignIds.length === 0) {
      toast.error("Please select at least one campaign")
      return
    }
    setLoading(true)
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
      const campaignIdsString = campaignIds.join(",")
      let url = `${BASE_URL}/campaign/admission_summary_report?campaign_ids=${campaignIdsString}`
      if (programLevel && programLevel !== "All") {
        url += `&program_level=${encodeURIComponent(programLevel)}`
      }
      if (gender && gender !== "All") {
        url += `&gender=${encodeURIComponent(gender)}`
      }

      const response = await fetch(url, requestOptions)
      const data = await response.json()
      if (response.ok) {
        setSummaryData(data)
        setFilteredData(data)
        toast.success("Summary report loaded successfully")
      } else {
        toast.error(data.error || "Failed to fetch admission summary")
        setSummaryData(null)
        setFilteredData(null)
      }
    } catch (error) {
      console.error("Error fetching admission summary:", error)
      toast.error("Error fetching admission summary")
      setSummaryData(null)
      setFilteredData(null)
    } finally {
      setLoading(false)
    }
  }

  // Handle campaign selection (multiple)
  const handleCampaignChange = (selectedOptions) => {
    setSelectedCampaigns(selectedOptions || [])
    setSummaryData(null)
    setFilteredData(null)
    setExpandedDepartments(new Set())
    setSearchTerm("")
  }

  const handleProgramLevelChange = (option) => {
    setProgramLevelFilter(option?.value || "All")
  }

  const handleGenderChange = (option) => {
    setGenderFilter(option?.value || "All")
  }

  // Handle fetch button click
  const handleFetchReport = () => {
    if (selectedCampaigns && selectedCampaigns.length > 0) {
      const campaignIds = selectedCampaigns.map((c) => c.value)
      fetchAdmissionSummary(campaignIds, programLevelFilter, genderFilter)
    }
  }

  // Filter data based on search term
  const filterData = (data, term) => {
    if (!data || !term.trim()) return data
    const searchLower = term.toLowerCase()
    const filteredDepartments =
      data.departments
        ?.filter((department) => {
          const departmentMatch = department.name.toLowerCase().includes(searchLower)
          const programMatch = department.programs?.some((program) => program.name.toLowerCase().includes(searchLower))
          return departmentMatch || programMatch
        })
        .map((department) => ({
          ...department,
          programs: department.programs?.filter(
            (program) =>
              department.name.toLowerCase().includes(searchLower) || program.name.toLowerCase().includes(searchLower),
          ),
        })) || []
    return {
      ...data,
      departments: filteredDepartments,
    }
  }

  // Handle search input change
  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchTerm(value)
    if (summaryData) {
      const filtered = filterData(summaryData, value)
      setFilteredData(filtered)
    }
  }

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("")
    setFilteredData(summaryData)
  }

  // Print functionality
  const handlePrint = () => {
    if (!summaryData) {
      toast.error("No report data available to print")
      return
    }
    // Create print content
    const printContent = generatePrintContent()
    const campaignNames = summaryData?.campaign_names?.join(", ") || "Report"
    const printWindow = window.open("", "_blank")
    printWindow.document.write(`
      <html>
        <head>
          <title>Admission Summary Report - ${campaignNames}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.4;
              color: #333;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px; 
            }
            .header h1 { 
              margin: 0 0 10px 0; 
              color: #2c3e50; 
              font-size: 28px; 
            }
            .header h2 { 
              margin: 0 0 15px 0; 
              color: #34495e; 
              font-size: 20px; 
              font-weight: normal; 
            }
            .header p { 
              margin: 5px 0; 
              color: #666; 
            }
            .campaign-list {
              margin: 10px 0;
              padding: 10px;
              background: #f8f9fa;
              border-radius: 4px;
            }
            .campaign-badge {
              display: inline-block;
              background: #007bff;
              color: white;
              padding: 4px 8px;
              margin: 2px;
              border-radius: 4px;
              font-size: 12px;
            }
            .filter-info {
              margin: 10px 0;
              padding: 8px;
              background: #e7f3ff;
              border-radius: 4px;
              font-size: 14px;
            }
            .summary-cards { 
              display: flex; 
              justify-content: space-around; 
              margin: 20px 0; 
              flex-wrap: wrap; 
            }
            .summary-card { 
              text-align: center; 
              padding: 15px; 
              border: 1px solid #ddd; 
              border-radius: 8px; 
              margin: 5px; 
              min-width: 150px; 
            }
            .summary-card h3 { 
              margin: 5px 0; 
              color: #2c3e50; 
              font-size: 24px; 
            }
            .summary-card p { 
              margin: 5px 0; 
              color: #666; 
              font-size: 14px; 
            }
            .section { 
              margin: 25px 0; 
              page-break-inside: avoid; 
            }
            .section-title { 
              background: #f8f9fa; 
              padding: 10px 15px; 
              border-left: 4px solid #007bff; 
              margin-bottom: 15px; 
              font-weight: bold; 
              font-size: 18px; 
            }
            .quota-list { 
              margin: 10px 0; 
            }
            .quota-item { 
              display: inline-block; 
              background: #e9ecef; 
              padding: 4px 8px; 
              margin: 2px; 
              border-radius: 4px; 
              font-size: 12px; 
            }
            .department-section { 
              margin: 20px 0; 
              border: 1px solid #ddd; 
              border-radius: 8px; 
              page-break-inside: avoid; 
            }
            .department-header { 
              background: #f8f9fa; 
              padding: 15px; 
              border-bottom: 1px solid #ddd; 
            }
            .department-title { 
              font-size: 18px; 
              font-weight: bold; 
              margin-bottom: 5px; 
            }
            .department-stats { 
              display: flex; 
              justify-content: space-around; 
              margin: 10px 0; 
              flex-wrap: wrap; 
            }
            .stat-item { 
              text-align: center; 
              margin: 5px; 
            }
            .stat-value { 
              font-weight: bold; 
              font-size: 16px; 
            }
            .stat-label { 
              font-size: 11px; 
              color: #666; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 15px 0; 
              font-size: 12px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f8f9fa; 
              font-weight: bold; 
            }
            .overall-summary table { 
              font-size: 14px; 
            }
            .overall-summary th { 
              background-color: #343a40; 
              color: white; 
            }
            .badge { 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px; 
              font-weight: bold; 
            }
            .bg-primary { background-color: #007bff; color: white; }
            .bg-success { background-color: #28a745; color: white; }
            .bg-warning { background-color: #ffc107; color: black; }
            .bg-danger { background-color: #dc3545; color: white; }
            .bg-info { background-color: #17a2b8; color: white; }
            .bg-secondary { background-color: #6c757d; color: white; }
            .quota-breakdown { 
              margin: 5px 0; 
            }
            .quota-badge { 
              display: inline-block; 
              margin: 1px; 
              background: #e9ecef; 
              padding: 2px 5px; 
              border-radius: 3px; 
              font-size: 10px; 
            }
            @media print {
              body { 
                margin: 0; 
                font-size: 12px; 
              }
              .no-print { 
                display: none !important; 
              }
              .page-break { 
                page-break-before: always; 
              }
              .department-section { 
                page-break-inside: avoid; 
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Admission Summary Report</h1>
            <h2>Department & Program Seat Segregation</h2>
            <p><strong>Campaigns:</strong></p>
            <div class="campaign-list">
              ${
                summaryData?.campaign_names?.map((name) => `<span class="campaign-badge">${name}</span>`).join("") ||
                "N/A"
              }
            </div>
            <div class="filter-info">
              <strong>Filters Applied:</strong> 
              Program Level: ${programLevelFilter} | 
              Gender: ${genderFilter}
            </div>
            <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
          ${printContent}
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  // Generate print content
  const generatePrintContent = () => {
    const grandTotal = summaryData.grand_total || {}
    const departments = summaryData.departments || []
    return `
      <div class="summary-cards">
        <div class="summary-card">
          <h3>${departments.length}</h3>
          <p>Total Departments</p>
        </div>
        <div class="summary-card">
          <h3>${departments.reduce((total, dept) => total + (dept.programs?.length || 0), 0)}</h3>
          <p>Total Programs</p>
        </div>
        <div class="summary-card">
          <h3>${grandTotal.total_seats || 0}</h3>
          <p>Total Seats</p>
        </div>
        <div class="summary-card">
          <h3>${grandTotal.filled_seats || 0}</h3>
          <p>Filled Seats</p>
        </div>
      </div>
      <div class="section">
        <div class="section-title">Available Quotas</div>
        <div class="quota-list">
          ${
            summaryData.quota_names?.map((quota) => `<span class="quota-item">${quota}</span>`).join("") ||
            "No quotas available"
          }
        </div>
      </div>
      <div class="section overall-summary">
        <div class="section-title">Overall Seat Segregation Summary</div>
        <table>
          <thead>
            <tr>
              <th>Total Seats</th>
              <th>Filled Seats</th>
              <th>Available Seats</th>
              <th>Fill Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>${grandTotal.total_seats || 0}</strong></td>
              <td><strong>${grandTotal.filled_seats || 0}</strong></td>
              <td><strong>${(grandTotal.total_seats || 0) - (grandTotal.filled_seats || 0)}</strong></td>
              <td><strong>${calculateFillPercentage(grandTotal.filled_seats || 0, grandTotal.total_seats || 0)}%</strong></td>
            </tr>
          </tbody>
        </table>
        <div>
          <strong>Quota-wise Breakdown:</strong>
          <div class="quota-breakdown">
            ${
              grandTotal.quotas
                ? Object.entries(grandTotal.quotas)
                    .map(
                      ([quotaName, quotaData]) =>
                        `<span class="quota-badge">${quotaName}: ${quotaData.filled_seats}/${quotaData.total_seats}</span>`,
                    )
                    .join(" ")
                : "No quota data available"
            }
          </div>
        </div>
      </div>
      <div class="section">
        <div class="section-title">Department-wise Seat Segregation</div>
        ${departments
          .map(
            (department) => `
          <div class="department-section">
            <div class="department-header">
              <div class="department-title">${department.name}</div>
              <div class="department-stats">
                <div class="stat-item">
                  <div class="stat-value">${department.total_seats}</div>
                  <div class="stat-label">Total Seats</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">${department.filled_seats}</div>
                  <div class="stat-label">Filled Seats</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">${department.total_seats - department.filled_seats}</div>
                  <div class="stat-label">Available</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">${calculateFillPercentage(department.filled_seats, department.total_seats)}%</div>
                  <div class="stat-label">Fill Rate</div>
                </div>
              </div>
              <div>
                <strong>Department Quota Breakdown:</strong>
                <div class="quota-breakdown">
                  ${
                    department.quotas
                      ? Object.entries(department.quotas)
                          .map(
                            ([quotaName, quotaData]) =>
                              `<span class="quota-badge">${quotaName}: ${quotaData.filled_seats}/${quotaData.total_seats}</span>`,
                          )
                          .join(" ")
                      : "No quota data"
                  }
                </div>
              </div>
            </div>
            ${
              department.programs && department.programs.length > 0
                ? `
              <div style="padding: 15px;">
                <strong>Program-wise Seat Segregation:</strong>
                <table>
                  <thead>
                    <tr>
                      <th>Program Name</th>
                      <th>Total Seats</th>
                      <th>Filled Seats</th>
                      <th>Available Seats</th>
                      <th>Fill Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${department.programs
                      .map(
                        (program) => `
                      <tr>
                        <td><strong>${program.name}</strong></td>
                        <td>${program.total_seats}</td>
                        <td>${program.filled_seats}</td>
                        <td>${program.total_seats - program.filled_seats}</td>
                        <td>${calculateFillPercentage(program.filled_seats, program.total_seats)}%</td>
                      </tr>
                    `,
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            `
                : ""
            }
          </div>
        `,
          )
          .join("")}
      </div>
    `
  }

  // Calculate fill percentage
  const calculateFillPercentage = (filled, total) => {
    return total > 0 ? Math.round((filled / total) * 100) : 0
  }

  // Get badge class based on fill percentage
  const getPercentageBadgeClass = (percentage) => {
    if (percentage >= 90) return "badge bg-success"
    if (percentage >= 70) return "badge bg-warning"
    return "badge bg-danger"
  }

  // Toggle department expansion
  const toggleDepartmentExpansion = (deptIndex) => {
    const newExpanded = new Set(expandedDepartments)
    if (newExpanded.has(deptIndex)) {
      newExpanded.delete(deptIndex)
    } else {
      newExpanded.add(deptIndex)
    }
    setExpandedDepartments(newExpanded)
  }

  // Render quota breakdown component
  const renderQuotaBreakdown = (quotas, size = "sm") => {
    if (!quotas || Object.keys(quotas).length === 0) {
      return <span className="text-muted">No quotas</span>
    }
    return (
      <div className="d-flex flex-wrap gap-1">
        {Object.entries(quotas).map(([quotaName, quotaData]) => (
          <span key={quotaName} className={`badge ${size === "lg" ? "bg-info" : "bg-secondary"} text-dark`}>
            {quotaName}: {quotaData.filled_seats}/{quotaData.total_seats}
          </span>
        ))}
      </div>
    )
  }

  // Load campaigns on component mount
  useEffect(() => {
    fetchAllCampaigns()
  }, [])

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Filter Card */}
            <Card className="mb-4">
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Admission Summary Report - Department & Program Seat Segregation
                </MDTypography>
                <Grid container spacing={2} alignItems="end">
                  <Grid item xs={12} md={5}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Select Campaigns (Multiple Selection Allowed)
                    </MDTypography>
                    <Select
                      name="campaigns"
                      options={allCampaigns.map((campaign) => ({
                        value: campaign.id,
                        label: campaign.name,
                      }))}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Select one or more campaigns"
                      value={selectedCampaigns}
                      onChange={handleCampaignChange}
                      isLoading={campaignLoading}
                      isMulti
                      isClearable
                    />
                    {selectedCampaigns && selectedCampaigns.length > 0 && (
                      <MDTypography variant="caption" color="text" mt={1}>
                        {selectedCampaigns.length} campaign(s) selected
                      </MDTypography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Program Level
                    </MDTypography>
                    <Select
                      name="programLevel"
                      options={programLevelOptions}
                      classNamePrefix="select"
                      placeholder="Select program level"
                      value={programLevelOptions.find((o) => o.value === programLevelFilter) || programLevelOptions[0]}
                      onChange={handleProgramLevelChange}
                      isClearable
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Gender
                    </MDTypography>
                    <Select
                      name="gender"
                      options={genderOptions}
                      classNamePrefix="select"
                      placeholder="Select gender"
                      value={genderOptions.find((o) => o.value === genderFilter) || genderOptions[0]}
                      onChange={handleGenderChange}
                      isClearable
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleFetchReport}
                      disabled={!selectedCampaigns || selectedCampaigns.length === 0 || loading}
                      startIcon={<AssessmentIcon />}
                      style={{ color: "white" }}
                      fullWidth
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Generate Report"}
                    </Button>
                  </Grid>
                </Grid>

                {/* Display active filters */}
                {(programLevelFilter !== "All" || genderFilter !== "All") && (
                  <MDBox mt={2}>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Active Filters:
                    </MDTypography>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {programLevelFilter !== "All" && (
                        <Chip
                          label={`Program Level: ${programLevelFilter}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {genderFilter !== "All" && (
                        <Chip
                          label={`Gender: ${genderFilter}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </div>
                  </MDBox>
                )}
              </MDBox>
            </Card>

            {/* Search and Print Controls */}
            {summaryData && (
              <Card className="mb-4">
                <MDBox p={3}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by department name or program name..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={handleClearSearch}
                                style={{ minWidth: "auto", padding: "4px" }}
                              >
                                <ClearIcon fontSize="small" />
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExportCsv}
                        startIcon={<DownloadIcon />}
                        fullWidth
                        style={{ color: "white" }}
                      >
                        Export CSV
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePrint}
                        startIcon={<PrintIcon />}
                        fullWidth
                        style={{ color: "white" }}
                      >
                        Print Report
                      </Button>
                    </Grid>
                  </Grid>
                  {searchTerm && (
                    <MDTypography variant="caption" color="text" mt={1}>
                      Showing results for {searchTerm} - {filteredData?.departments?.length || 0} departments found
                    </MDTypography>
                  )}
                </MDBox>
              </Card>
            )}

            {/* Report Content */}
            {loading ? (
              <Card>
                <MDBox display="flex" justifyContent="center" p={5}>
                  <CircularProgress />
                </MDBox>
              </Card>
            ) : filteredData ? (
              <div id="printable-report">
                {/* Summary Cards */}
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <MDBox p={2} textAlign="center">
                        <SchoolIcon fontSize="large" color="primary" />
                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                          {summaryData.departments?.length || 0}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Departments
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <MDBox p={2} textAlign="center">
                        <BookIcon fontSize="large" color="secondary" />
                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                          {summaryData.departments?.reduce((total, dept) => total + (dept.programs?.length || 0), 0) ||
                            0}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Programs
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <MDBox p={2} textAlign="center">
                        <PeopleIcon fontSize="large" color="info" />
                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                          {summaryData.grand_total?.total_seats || 0}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Total Seats
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <MDBox p={2} textAlign="center">
                        <TrendingUpIcon fontSize="large" color="success" />
                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                          {summaryData.grand_total?.filled_seats || 0}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Filled Seats
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>

                {/* Campaign Info */}
                <Card className="mb-4">
                  <MDBox p={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={2}>
                      Selected Campaigns ({summaryData.campaign_names?.length || 0})
                    </MDTypography>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {summaryData.campaign_names?.map((name, index) => (
                        <Chip key={index} label={name} color="primary" variant="outlined" />
                      ))}
                    </div>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={2}>
                      Available Quotas:
                    </MDTypography>
                    <div className="d-flex flex-wrap gap-2">
                      {summaryData.quota_names?.map((quota, index) => (
                        <span key={index} className="badge bg-primary">
                          {quota}
                        </span>
                      ))}
                    </div>
                  </MDBox>
                </Card>

                {/* Grand Total Summary */}
                <Card className="mb-4">
                  <MDBox p={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={3}>
                      Overall Seat Segregation Summary
                    </MDTypography>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="table-responsive">
                          <table className="table table-striped">
                            <thead className="table-dark">
                              <tr>
                                <th>Total Seats</th>
                                <th>Filled Seats</th>
                                <th>Available Seats</th>
                                <th>Fill Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>
                                  <strong className="text-primary">{summaryData.grand_total?.total_seats || 0}</strong>
                                </td>
                                <td>
                                  <strong className="text-success">{summaryData.grand_total?.filled_seats || 0}</strong>
                                </td>
                                <td>
                                  <strong className="text-warning">
                                    {(summaryData.grand_total?.total_seats || 0) -
                                      (summaryData.grand_total?.filled_seats || 0)}
                                  </strong>
                                </td>
                                <td>
                                  <span
                                    className={getPercentageBadgeClass(
                                      calculateFillPercentage(
                                        summaryData.grand_total?.filled_seats || 0,
                                        summaryData.grand_total?.total_seats || 0,
                                      ),
                                    )}
                                  >
                                    {calculateFillPercentage(
                                      summaryData.grand_total?.filled_seats || 0,
                                      summaryData.grand_total?.total_seats || 0,
                                    )}
                                    %
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                          Quota-wise Breakdown:
                        </MDTypography>
                        {renderQuotaBreakdown(summaryData.grand_total?.quotas, "lg")}
                      </div>
                    </div>
                  </MDBox>
                </Card>

                {/* Department-wise Seat Segregation */}
                <Card className="mb-4">
                  <MDBox p={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={3}>
                      Department-wise Seat Segregation
                      {searchTerm && <span className="text-muted"> (Filtered Results)</span>}
                    </MDTypography>

                    {filteredData.departments?.length === 0 ? (
                      <MDBox display="flex" flexDirection="column" alignItems="center" p={3}>
                        <SearchIcon fontSize="large" color="disabled" />
                        <MDTypography variant="body1" mt={2} color="text">
                          No departments or programs found matching {searchTerm}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      filteredData.departments?.map((department, deptIndex) => (
                        <div key={deptIndex} className="mb-4">
                          <Accordion
                            expanded={expandedDepartments.has(deptIndex)}
                            onChange={() => toggleDepartmentExpansion(deptIndex)}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                <div>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    {department.name}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text">
                                    {department.programs?.length || 0} Programs
                                  </MDTypography>
                                </div>
                                <div className="d-flex gap-3">
                                  <div className="text-center">
                                    <small className="text-muted d-block">Total</small>
                                    <strong className="text-primary">{department.total_seats}</strong>
                                  </div>
                                  <div className="text-center">
                                    <small className="text-muted d-block">Filled</small>
                                    <strong className="text-success">{department.filled_seats}</strong>
                                  </div>
                                  <div className="text-center">
                                    <small className="text-muted d-block">Available</small>
                                    <strong className="text-warning">
                                      {department.total_seats - department.filled_seats}
                                    </strong>
                                  </div>
                                  <div className="text-center">
                                    <small className="text-muted d-block">Fill Rate</small>
                                    <span
                                      className={getPercentageBadgeClass(
                                        calculateFillPercentage(department.filled_seats, department.total_seats),
                                      )}
                                    >
                                      {calculateFillPercentage(department.filled_seats, department.total_seats)}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </AccordionSummary>

                            <AccordionDetails>
                              {/* Department Quota Breakdown */}
                              <div className="mb-3">
                                <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                                  Department Quota Breakdown:
                                </MDTypography>
                                {renderQuotaBreakdown(department.quotas)}
                              </div>

                              {/* Programs Table */}
                              {department.programs && department.programs.length > 0 && (
                                <div>
                                  <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                                    Program-wise Seat Segregation:
                                  </MDTypography>
                                  <div className="table-responsive">
                                    <table className="table table-striped table-hover table-sm">
                                      <thead className="table-info">
                                        <tr>
                                          <th>Program Name</th>
                                          <th>Total Seats</th>
                                          <th>Filled Seats</th>
                                          <th>Available Seats</th>
                                          <th>Fill Rate</th>
                                          <th>Quota Breakdown</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {department.programs.map((program, progIndex) => (
                                          <tr key={progIndex}>
                                            <td>
                                              <strong>{program.name}</strong>
                                            </td>
                                            <td>
                                              <span className="badge bg-primary">{program.total_seats}</span>
                                            </td>
                                            <td>
                                              <span className="badge bg-success">{program.filled_seats}</span>
                                            </td>
                                            <td>
                                              <span className="badge bg-warning text-dark">
                                                {program.total_seats - program.filled_seats}
                                              </span>
                                            </td>
                                            <td>
                                              <span
                                                className={getPercentageBadgeClass(
                                                  calculateFillPercentage(program.filled_seats, program.total_seats),
                                                )}
                                              >
                                                {calculateFillPercentage(program.filled_seats, program.total_seats)}%
                                              </span>
                                            </td>
                                            <td>{renderQuotaBreakdown(program.quotas)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Add Collapse Button at the top */}
                              <div className="d-flex justify-content-end mb-3">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => toggleDepartmentExpansion(deptIndex)}
                                  startIcon={<ExpandLessIcon />}
                                  style={{
                                    borderColor: "#1976d2",
                                    color: "#1976d2",
                                    textTransform: "none",
                                  }}
                                >
                                  Collapse
                                </Button>
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        </div>
                      ))
                    )}
                  </MDBox>
                </Card>
              </div>
            ) : (
              <Card>
                <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                  <img
                    src={nodata || "/placeholder.svg"}
                    style={{
                      width: "20%",
                      height: "20%",
                    }}
                    alt="No Data"
                  />
                  <MDTypography variant="body1" mt={2}>
                    No data available. Please select campaign(s) and generate the report.
                  </MDTypography>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default AdmissionSummaryReport